'use client';

/**
 * NotificationDetailSlideOver — detail view for a single item from the
 * notification bell's preview panel, rendered in the canonical SlideOver
 * chrome. One variant per inbox item kind:
 *
 *   notification — /api/v1/notifications entry (title/message/work item)
 *   mention      — pending @mention from a thread or work-item comment
 *   approval     — leave / overtime / shift-violation / device-admin request
 *   task         — managed task due today or overdue
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import {
  AtSign,
  Bell,
  CheckSquare,
  CircleAlert,
  ExternalLink,
  MessageSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import SlideOver, {
  SlideOverButton,
  SlideOverFooter,
} from '@/components/SlideOver';
import type { Notification } from './NotificationBell';
import {
  getApprovalDisplay,
  type ManagedApprovalRequest,
  type PendingTagNotification,
} from './useAttentionInbox';
import type { WorkItem } from '@/components/UniversalWorkItems';

export type InboxItem =
  | { kind: 'notification'; notification: Notification }
  | { kind: 'mention'; mention: PendingTagNotification }
  | { kind: 'approval'; approval: ManagedApprovalRequest }
  | { kind: 'task'; task: WorkItem };

interface NotificationDetailSlideOverProps {
  item: InboxItem | null;
  onClose: () => void;
  /** 'Open thread' / 'Open work item' for mentions. */
  onOpenMention: (mention: PendingTagNotification) => void;
  onDismissMention: (mention: PendingTagNotification) => void;
  onApprove: (request: ManagedApprovalRequest) => void;
  onReject: (request: ManagedApprovalRequest) => void;
  /** Deep-link into the module that owns the approval request. */
  onOpenApproval: (request: ManagedApprovalRequest) => void;
  approvalActionPending: boolean;
  /** 'Open task' for due tasks and notification work-item links. */
  onOpenWorkItem: (workItemId: string) => void;
  /** 'Open' for generic notification links. */
  onOpenLink: (link: string) => void;
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <div className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{children}</div>
    </div>
  );
}

function MentionDetail({ mention }: { mention: PendingTagNotification }) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/60">
        <div className="flex flex-wrap items-center gap-2">
          <MessageSquare className="h-4 w-4 text-purple-500" />
          <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {mention.thread_name}
          </span>
        </div>
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
          <span className="font-medium">{mention.sender_name}</span>
          {' · '}
          {mention.message_preview}
        </p>
      </div>
      <DetailRow label="Tagged">
        {new Date(mention.timestamp).toLocaleString()}
      </DetailRow>
    </div>
  );
}

function ApprovalDetail({
  approval,
  accent,
}: {
  approval: ManagedApprovalRequest;
  accent: string;
}) {
  const display = getApprovalDisplay(approval, accent);
  const {
    requestLabel,
    accentColor,
    summary,
    overtimeRequest,
    sessionStatus,
    sessionStatusColor,
    sessionStatusLabel,
    showCliffWarning,
    showApprovalDeadline,
  } = display;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/60">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {approval.employee_name}
          </span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize"
            style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
          >
            {requestLabel}
          </span>
          {sessionStatus && sessionStatusColor && sessionStatusLabel && (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ backgroundColor: `${sessionStatusColor}20`, color: sessionStatusColor }}
              title={sessionStatus === 'nullified' ? 'Overtime was not approved by deadline' : undefined}
            >
              {sessionStatusLabel}
            </span>
          )}
        </div>
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">{summary}</p>
        {showCliffWarning && (
          <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-600">
            <CircleAlert className="h-3.5 w-3.5" />
            Cliff not met — no overtime claimable
          </p>
        )}
        {showApprovalDeadline && overtimeRequest?.approval_deadline && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Approval deadline: {format(new Date(overtimeRequest.approval_deadline), 'd MMM yyyy HH:mm')}
          </p>
        )}
      </div>

      {approval.reason && (
        <DetailRow label="Reason">{approval.reason}</DetailRow>
      )}
      <DetailRow label="Submitted">
        {new Date(approval.requested_at).toLocaleString()}
      </DetailRow>
    </div>
  );
}

function TaskDetail({ task }: { task: WorkItem }) {
  const due = task.due_date ? new Date(task.due_date) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue = due ? due.getTime() < today.getTime() : false;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/60">
        <div className="flex flex-wrap items-center gap-2">
          <CheckSquare className="h-4 w-4 text-blue-500" />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {task.item_number}
          </span>
          {task.status && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold capitalize text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {task.status.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">{task.title}</p>
      </div>

      {due && (
        <DetailRow label="Due date">
          <span className={cn(overdue && 'font-semibold text-red-600 dark:text-red-400')}>
            {format(due, 'd MMM yyyy')}
            {overdue && ' — overdue'}
          </span>
        </DetailRow>
      )}
      {task.priority && <DetailRow label="Priority"><span className="capitalize">{task.priority}</span></DetailRow>}
    </div>
  );
}

function NotificationDetail({ notification }: { notification: Notification }) {

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/60">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {notification.title}
        </p>
        {notification.message && (
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            {notification.message}
          </p>
        )}
      </div>

      {notification.work_item && (
        <DetailRow label="Work item">
          {notification.work_item.item_number} · {notification.work_item.title}
        </DetailRow>
      )}
      <DetailRow label="Received">
        {new Date(notification.created_at).toLocaleString()}
      </DetailRow>
    </div>
  );
}

export default function NotificationDetailSlideOver({
  item,
  onClose,
  onOpenMention,
  onDismissMention,
  onApprove,
  onReject,
  onOpenApproval,
  approvalActionPending,
  onOpenWorkItem,
  onOpenLink,
}: NotificationDetailSlideOverProps) {
  const { clientBranding, currentTheme } = useTheme();
  const accent = clientBranding?.primary_color || currentTheme.colors.primary;

  // Portal to body — the bell lives inside the sticky header, whose stacking
  // context + backdrop-blur would otherwise trap the fixed panel under the
  // notification dropdown.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  if (!item || !mounted) return null;

  const footer = (() => {
    if (item.kind === 'mention') {
      const m = item.mention;
      return (
        <SlideOverFooter summary={m.source === 'work_item_comment' ? 'Work-item comment tag' : 'Message tag'}>
          <SlideOverButton variant="primary" onClick={() => onOpenMention(m)}>
            {m.source === 'work_item_comment' ? 'Open work item' : 'Open thread'}
          </SlideOverButton>
          <SlideOverButton variant="outline" onClick={() => onDismissMention(m)}>
            Dismiss
          </SlideOverButton>
        </SlideOverFooter>
      );
    }
    if (item.kind === 'approval') {
      const request = item.approval;
      const display = getApprovalDisplay(request, accent);
      return (
        <SlideOverFooter summary={`${display.requestLabel} request`}>
          <SlideOverButton
            variant="primary"
            spinning={approvalActionPending}
            disabled={approvalActionPending}
            onClick={() => onApprove(request)}
          >
            {display.isShiftViolation ? 'Acknowledge' : 'Approve'}
          </SlideOverButton>
          <SlideOverButton
            variant="outline"
            disabled={approvalActionPending}
            onClick={() => onReject(request)}
          >
            {display.isShiftViolation ? 'Dismiss' : 'Reject'}
          </SlideOverButton>
          <SlideOverButton variant="outline" icon={ExternalLink} onClick={() => onOpenApproval(request)}>
            Open
          </SlideOverButton>
        </SlideOverFooter>
      );
    }
    if (item.kind === 'task') {
      const task = item.task;
      const due = task.due_date ? new Date(task.due_date) : null;
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const overdue = due ? due.getTime() < todayStart.getTime() : false;
      return (
        <SlideOverFooter summary={overdue ? 'Task is overdue' : 'Task is due today'}>
          <SlideOverButton
            variant="primary"
            icon={ExternalLink}
            onClick={() => onOpenWorkItem(String(task.id))}
          >
            Open task
          </SlideOverButton>
        </SlideOverFooter>
      );
    }
    const n = item.notification;
    if (!n.link && !n.work_item_id) return undefined;
    return (
      <SlideOverFooter>
        <SlideOverButton
          variant="primary"
          icon={ExternalLink}
          onClick={() => {
            if (n.link) {
              onOpenLink(n.link);
            } else if (n.work_item_id) {
              onOpenWorkItem(n.work_item_id);
            }
          }}
        >
          Open
        </SlideOverButton>
      </SlideOverFooter>
    );
  })();

  const header =
    item.kind === 'mention'
      ? { icon: AtSign, title: 'Mention', subtitle: item.mention.thread_name }
      : item.kind === 'approval'
        ? {
            icon: CircleAlert,
            title: 'Approval request',
            subtitle: item.approval.employee_name,
          }
        : item.kind === 'task'
          ? { icon: CheckSquare, title: 'Task due', subtitle: item.task.item_number || 'Task' }
          : { icon: Bell, title: 'Notification', subtitle: undefined };

  return createPortal(
    <SlideOver
      onClose={onClose}
      size="default"
      icon={header.icon}
      title={header.title}
      subtitle={header.subtitle}
      zIndex={120}
      footer={footer}
    >
      {item.kind === 'mention' && (
        <MentionDetail mention={item.mention} />
      )}
      {item.kind === 'approval' && (
        <ApprovalDetail approval={item.approval} accent={accent} />
      )}
      {item.kind === 'task' && (
        <TaskDetail task={item.task} />
      )}
      {item.kind === 'notification' && (
        <NotificationDetail notification={item.notification} />
      )}
    </SlideOver>,
    document.body,
  );
}
